import React, { useEffect, useState } from "react";
import { Flex, Popconfirm, Spin } from "antd";
import Table from 'components/Common/Table';
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import * as actions from "store/actions/AuthActions";
import useScrollBottom from "CustomHooks/useScrollBottom";
import FilterBar from 'components/Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';

// The search lives in the URL so a filtered list can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
};

const TemplatesTable = () => {
  const bottom = useScrollBottom();

  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const searchTerm = filters.search || "";
  const setSearchTerm = (value) => setFilters({ search: value });
  const [searchLive, setSearchLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchLive(searchTerm), [searchTerm]);

  const [totalRows, setTotalRows] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchTemplates = async (page) => {
    setLoading(true);
    try {
      const response = await actions.loadTemplates(page, searchTerm);
      if (response.status == 200) {
        const data = response.data;
        if (page) {
          setDataSource([...dataSource, ...data.results]);
          setTotalRows(data.count);
        } else {
          setDataSource([...data.results]);
          setTotalRows(data.count);
        }
      }
    } catch (e) {}
    setLoading(false);
  };

  const onSearch = (value) => {
    if (searchTerm === value) {
    } else {
      setSearchTerm(value);
      setDataSource([]);
      setPageNumber(0);
    }
  };

  useEffect(() => {
    let p = pageNumber + 1;
    // console.log({ next_p: p, dataSource, totalRows });
    if (p === 1) {
      fetchTemplates(p);
      setPageNumber(p);
    } else if (dataSource && dataSource.length < totalRows) {
      fetchTemplates(p);
      setPageNumber(p);
    }
  }, [bottom, searchTerm]);

  const handleDelete = async (id) => {
    const newData = dataSource.find((item) => item.id === id);
    // setDataSource(newData);
    const response = await actions.deleteUsers(newData.id);
    console.log(response);
    if (response) {
      fetchTemplates();
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        "selectedRows: ",
        selectedRows
      );
    },
  };

  const defaultColumns = [
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      width: 220,
    },
    {
      title: "DESCRPITION",
      dataIndex: "description",
      key: "description",
      width: 220,
    },
    {
      title: "TEMPLATE TYPE",
      dataIndex: "template_type",
      key: "template_type",
      width: 220,
    },
    {
      title: "Actions",
      dataIndex: "Actions",
      key: "Actions",
      width: 220,
      render: (_, record) => {
        // console.log(record);
        return dataSource?.length >= 1 ? (
          <Flex gap="middle">
            <div
              style={{ cursor: "pointer" }}
              onClick={() => {
                console.log("View Document");
              }}
            >
              <EyeOutlined />
            </div>
            <a>
              <EditOutlined
                style={{ verticalAlign: "middle" }}
                onClick={() => navigate(`/templates/edit/${record.id}/`)}
              />
            </a>
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => handleDelete(record.id)}
            >
              <a>
                <DeleteOutlined />
              </a>
            </Popconfirm>
          </Flex>
        ) : null;
      },
    },
  ];

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom) {
      let p = pageNumber + 1;
      // console.log({ next_p: p, dataSource, totalRows });
      if (p === 1) {
        fetchTemplates(p);
        setPageNumber(p);
      } else if (dataSource && dataSource.length < totalRows) {
        fetchTemplates(p);
        setPageNumber(p);
      }
    }
  };

  const [selectionType] = useState("checkbox");

  return (
    <div style={{ padding: 10 }}>
      <Flex gap="middle" justify="space-between">
        <Flex gap="middle" align="center">
          <div>
            ({dataSource?.length} / {totalRows})
          </div>
          <FilterBar
            searchValue={searchLive}
            onSearchChange={setSearchLive}
            onSearch={onSearch}
            searchPlaceholder="Search templates"
            onRefresh={() => {
                setDataSource([]);
                setPageNumber(0);
                fetchTemplates(1);
                setPageNumber(1);
                }}
            refreshing={loading}
            addLabel="Add Template"
            addIcon={<UserAddOutlined />}
            onAdd={() => navigate("/templates/new")}
/>
        </Flex>
      </Flex>
      <Flex>
        <div
          style={{ height: "75vh", overflow: "hidden", marginTop: 20 }}
          onScroll={handleScroll}
        >
          <Table
            rowSelection={{
              type: selectionType,
              ...rowSelection,
            }}
            bordered
            size="small"
            dataSource={dataSource}
            columns={columns}
            rowKey={(row) => row.id}
            pagination={false}
            scrollToFirstRowOnChange
            stickyScrollBarBg={"rgba(0, 0, 0, 1)"}
            scroll={{
              x: "max-content", // Horizontal scroll
              y: "calc(100vh - 350px)", // Vertical scroll
            }}
            sticky
          />
          <div style={{ margin: 20 }}>
            {loading && (
              <>
                <Spin tip="Loading" size="small">
                  <div className="content" />
                </Spin>
              </>
            )}
          </div>
        </div>
      </Flex>
    </div>
  );
};
export default TemplatesTable;
